import { useState, type FormEvent } from 'react';
import type { Brand, ProductSortField, SortDirection } from '../types/catalog.js';

export interface ListingFilterState {
  brandId?: string;
  priceMinPaise?: number;
  priceMaxPaise?: number;
  inStockOnly?: boolean;
  isFeatured?: boolean;
}

export interface ProductFiltersProps {
  brands: Brand[];
  value: ListingFilterState;
  onChange: (next: ListingFilterState) => void;
  sortField: ProductSortField;
  sortDirection: SortDirection;
  onSortChange: (field: ProductSortField, direction: SortDirection) => void;
  /** Renders as a vertical panel (sidebar/drawer) vs the compact sort-only bar isn't needed here -- always vertical, callers wrap it for desktop/mobile placement. */
  idPrefix: string;
}

export const SORT_OPTIONS: { field: ProductSortField; label: string }[] = [
  { field: 'popularity', label: 'Popularity' },
  { field: 'createdAt', label: 'Newest' },
  { field: 'publishedAt', label: 'Recently published' },
  { field: 'name', label: 'Name' },
];

/**
 * Filter + sort controls shared by the desktop sidebar and the mobile
 * drawer in `ProductListing`. Price range uses local rupee-denominated
 * input state committed on submit (not on every keystroke) so typing a
 * price doesn't fire a request per digit; every other control commits
 * immediately since it's a single discrete change.
 */
export function ProductFilters({ brands, value, onChange, sortField, sortDirection, onSortChange, idPrefix }: ProductFiltersProps) {
  const [minInput, setMinInput] = useState(value.priceMinPaise !== undefined ? String(value.priceMinPaise / 100) : '');
  const [maxInput, setMaxInput] = useState(value.priceMaxPaise !== undefined ? String(value.priceMaxPaise / 100) : '');

  function submitPriceRange(event: FormEvent) {
    event.preventDefault();
    const min = minInput.trim() === '' ? undefined : Math.round(Number(minInput) * 100);
    const max = maxInput.trim() === '' ? undefined : Math.round(Number(maxInput) * 100);
    onChange({ ...value, priceMinPaise: Number.isFinite(min) ? min : undefined, priceMaxPaise: Number.isFinite(max) ? max : undefined });
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <label htmlFor={`${idPrefix}-sort-field`} className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">
          Sort by
        </label>
        <div className="flex gap-2">
          <select
            id={`${idPrefix}-sort-field`}
            value={sortField}
            onChange={(event) => onSortChange(event.target.value as ProductSortField, sortDirection)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.field} value={option.field}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onSortChange(sortField, sortDirection === 'asc' ? 'desc' : 'asc')}
            aria-label={sortDirection === 'asc' ? 'Sort ascending, switch to descending' : 'Sort descending, switch to ascending'}
            className="focus-visible:ring-brand shrink-0 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-brand`} className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">
          Brand
        </label>
        <select
          id={`${idPrefix}-brand`}
          value={value.brandId ?? ''}
          onChange={(event) => onChange({ ...value, brandId: event.target.value || undefined })}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        >
          <option value="">All brands</option>
          {brands.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
            </option>
          ))}
        </select>
      </div>

      <form onSubmit={submitPriceRange} className="flex flex-col gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Price range</span>
        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor={`${idPrefix}-price-min`}>
            Minimum price
          </label>
          <input
            id={`${idPrefix}-price-min`}
            type="number"
            min={0}
            inputMode="decimal"
            placeholder="Min"
            value={minInput}
            onChange={(event) => setMinInput(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
          <span className="text-gray-400">–</span>
          <label className="sr-only" htmlFor={`${idPrefix}-price-max`}>
            Maximum price
          </label>
          <input
            id={`${idPrefix}-price-max`}
            type="number"
            min={0}
            inputMode="decimal"
            placeholder="Max"
            value={maxInput}
            onChange={(event) => setMaxInput(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <button
          type="submit"
          className="focus-visible:ring-brand self-start rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
        >
          Apply price
        </button>
      </form>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={value.inStockOnly ?? false}
          onChange={(event) => onChange({ ...value, inStockOnly: event.target.checked || undefined })}
          className="focus-visible:ring-brand h-4 w-4 rounded border-gray-300"
        />
        In stock only
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={value.isFeatured ?? false}
          onChange={(event) => onChange({ ...value, isFeatured: event.target.checked || undefined })}
          className="focus-visible:ring-brand h-4 w-4 rounded border-gray-300"
        />
        Featured only
      </label>

      <button
        type="button"
        onClick={() => onChange({})}
        className="focus-visible:ring-brand self-start text-xs font-semibold text-gray-500 underline hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2"
      >
        Clear filters
      </button>
    </div>
  );
}
